import { callGeotabAPI } from './geotab-client';
import {
  DiagnosticFuelLevelId,
  DiagnosticOdometerId,
  DiagnosticOdometerAdjustmentId,
  DiagnosticBatteryVoltageId,
  DiagnosticExternalBatteryVoltageId,
  BATTERY_MIN_V,
  BATTERY_MAX_V,
  SPEED_FALLBACK_IDS,
  IGNITION_FALLBACK_IDS,
} from './diagnostics';
import { normalizeFuelLevel } from '../utils/fuel';
import type {
  Device,
  DeviceStatusInfo,
  Diagnostic,
  FaultData,
  StatusData,
  User,
  Group,
  VehicleData,
} from './types';

// ── Diagnostic value lookup ───────────────────────────────────────────────

export function findDiagnosticValue(
  statusData: StatusData[],
  searchTermsOrIds: string[]
): StatusData | null {
  for (const term of searchTermsOrIds) {
    const found = statusData.find((s) => {
      if (!s.diagnostic) return false;
      if (s.diagnostic.id === term) return true;
      if (s.diagnostic.name?.toLowerCase().includes(term.toLowerCase())) return true;
      return false;
    });
    if (found) return found;
  }
  return null;
}

// ── Fault source classification ───────────────────────────────────────────

function classifyFaultSource(fault: FaultData, diagnosticMap: Map<string, Diagnostic>): void {
  const controllerId = (fault.controller?.id ?? '').toLowerCase();
  const diagDef = fault.diagnostic?.id ? diagnosticMap.get(fault.diagnostic.id) : null;
  const sourceId = (diagDef?.source?.id ?? '').toLowerCase();
  const failureMode = (fault.failureMode?.name ?? '').toLowerCase();

  const isObdProtocol =
    sourceId.includes('obd') || sourceId.includes('j1939') || sourceId.includes('j1708');
  const isGoDevice =
    controllerId.includes('godevice') || controllerId.includes('telematics');
  const isTelematicsFailureMode =
    failureMode.includes('telematics device fault') ||
    failureMode.includes('telematics fault');

  if (isGoDevice || isTelematicsFailureMode) {
    fault.source = 'telematics';
  } else if (isObdProtocol) {
    fault.source = 'vehicle';
  } else if (sourceId && !isObdProtocol) {
    fault.source = 'telematics';
  } else {
    fault.source = 'vehicle';
  }

  fault.protocol = diagDef?.source?.id ?? '';
}

// ── Main data loader ──────────────────────────────────────────────────────

export async function loadFleetData(): Promise<VehicleData[]> {
  // 1. Devices — filter to active only with serialNumber
  const allDevices = await callGeotabAPI<Device[]>('Get', { typeName: 'Device' });
  const devices = allDevices.filter((device) => {
    if (device.activeTo && new Date(device.activeTo) <= new Date()) return false;
    if (!device.serialNumber) return false;
    return true;
  });

  // 2. DeviceStatusInfo — isDriving for engine state, speed (rule #1)
  const deviceStatusInfoList = await callGeotabAPI<DeviceStatusInfo[]>('Get', {
    typeName: 'DeviceStatusInfo',
  });
  const deviceStatusInfoMap = new Map<string, DeviceStatusInfo>();
  deviceStatusInfoList.forEach((dsi) => {
    if (dsi.device?.id) deviceStatusInfoMap.set(dsi.device.id, dsi);
  });

  // 3. Diagnostic definitions — build lookup map
  const diagnostics = await callGeotabAPI<Diagnostic[]>('Get', { typeName: 'Diagnostic' });
  const diagnosticMap = new Map<string, Diagnostic>();
  diagnostics.forEach((d) => diagnosticMap.set(d.id, d));

  // Enrich DSI statusData with diagnostic names
  deviceStatusInfoList.forEach((dsi) => {
    if (Array.isArray(dsi.statusData)) {
      dsi.statusData.forEach((s) => {
        if (s.diagnostic?.id) {
          const d = diagnosticMap.get(s.diagnostic.id);
          if (d) {
            s.diagnostic.name = d.name;
            s.diagnostic.code = d.code;
            s.diagnostic.unitOfMeasure = d.unitOfMeasure;
          }
        }
      });
    }
  });

  // 4. FaultData — unique active fault CODES per vehicle (not raw event count).
  //
  // Geotab creates a new FaultData record every ignition cycle / communication interval
  // while a fault code persists on the ECU. A single fault code can produce hundreds of
  // records over 30 days. Dismissing in Geotab sets dismissDateTime on specific historical
  // records, but new records for the same code keep arriving undismissed — which is why
  // dismissed faults kept reappearing.
  //
  // Fix: deduplicate by device + diagnostic after fetching. For each fault code on each
  // vehicle we keep ONLY the most recent record, then filter that record's state:
  //   !dismissDateTime → the latest occurrence has been acknowledged by the operator
  //   activeTo == null → the ECU has not cleared the code (null = still active)
  //
  // If a code is dismissed and then re-triggers, the newer record has no dismissDateTime
  // and correctly reappears — this is intentional, not a bug.
  //
  // Window: 30 days. Any vehicle in active use will have fault records within 30 days.
  // A shorter window is better here: Geotab returns records ascending by date, so with
  // a tighter window the 50 000-record cap is less likely to cut off the recent records
  // that carry the correct dismissed/active state.
  // resultsLimit: 50 000 (up from 10 000).
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const allFaultData = await callGeotabAPI<FaultData[]>('Get', {
    typeName: 'FaultData',
    search: { fromDate: thirtyDaysAgo.toISOString() },
    resultsLimit: 50000,
  });

  // Count triggers and collect event dates per device+diagnostic key.
  // allFaultData arrives ascending by date, so dates[] is already in ascending order.
  // We cap at 50 most-recent dates per key to bound memory on high-frequency faults.
  const triggersByKey = new Map<string, { count: number; dates: string[] }>();
  for (const f of allFaultData) {
    const key = `${f.device?.id}__${f.diagnostic?.id ?? f.id}`;
    const entry = triggersByKey.get(key);
    if (entry) {
      entry.count++;
      entry.dates.push(f.dateTime);
    } else {
      triggersByKey.set(key, { count: 1, dates: [f.dateTime] });
    }
  }
  // Trim to 50 most recent (tail of ascending array), then reverse to descending.
  for (const entry of triggersByKey.values()) {
    if (entry.dates.length > 50) entry.dates = entry.dates.slice(-50);
    entry.dates.reverse();
  }

  // Deduplicate: most recent record per vehicle + fault code.
  const latestByKey = new Map<string, FaultData>();
  for (const f of allFaultData) {
    const key = `${f.device?.id}__${f.diagnostic?.id ?? f.id}`;
    const prev = latestByKey.get(key);
    if (!prev || f.dateTime > prev.dateTime) latestByKey.set(key, f);
  }
  const faultData = [...latestByKey.values()].filter(
    (f) => !f.dismissDateTime && f.activeTo == null,
  );

  faultData.forEach((f) => {
    const key = `${f.device?.id}__${f.diagnostic?.id ?? f.id}`;
    const td = triggersByKey.get(key);
    f.count = td?.count ?? 1;
    f.triggerDates = td?.dates ?? [];
    if (f.diagnostic?.id) {
      const d = diagnosticMap.get(f.diagnostic.id);
      if (d) {
        f.diagnostic.name = d.name;
        f.diagnostic.code = d.code;
      }
    }
    classifyFaultSource(f, diagnosticMap);
  });

  // 5. StatusData for fuel, odometer (fallback), battery — parallel fetches
  //
  // Odometer strategy:
  //   Primary  — DSI.statusData: already fetched (step 2), zero extra API calls, no record
  //              limit.  We search by both KnownId strings AND the name "odometer" to catch
  //              databases that use different internal IDs for GPS-based odometer.
  //   Fallback — DiagnosticOdometerId bulk query: this diagnostic is logged only at ignition
  //              ON/OFF (not every 2 km), so 1 372 vehicles × 3 days stays under 50 000
  //              records and the ascending result order gives us the most-recent readings.
  //              Note: DiagnosticOdometerAdjustmentId is expensive and not supported on all
  //              databases, so we use the standard ECM odometer diagnostic here.
  const sevenDaysAgo  = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const threeDaysAgo  = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();

  const [users, groups, fuelStatusData, odometerFallbackData, batteryStatusData, batteryExtData] =
    await Promise.all([
      callGeotabAPI<User[]>('Get', { typeName: 'User' }),
      callGeotabAPI<Group[]>('Get', { typeName: 'Group' }),
      callGeotabAPI<StatusData[]>('Get', {
        typeName: 'StatusData',
        search: { diagnosticSearch: { id: DiagnosticFuelLevelId }, fromDate: sevenDaysAgo },
        resultsLimit: 50000,
      }),
      // DiagnosticOdometerId (ignition-event logging only) stays well under 50 000 records
      // even for large fleets over 3 days, so latestPerDevice gets the most-recent reading.
      callGeotabAPI<StatusData[]>('Get', {
        typeName: 'StatusData',
        search: { diagnosticSearch: { id: DiagnosticOdometerId }, fromDate: threeDaysAgo },
        resultsLimit: 50000,
      }).catch(() => [] as StatusData[]),
      callGeotabAPI<StatusData[]>('Get', {
        typeName: 'StatusData',
        search: { diagnosticSearch: { id: DiagnosticBatteryVoltageId }, fromDate: sevenDaysAgo },
        resultsLimit: 50000,
      }),
      callGeotabAPI<StatusData[]>('Get', {
        typeName: 'StatusData',
        search: { diagnosticSearch: { id: DiagnosticExternalBatteryVoltageId }, fromDate: sevenDaysAgo },
        resultsLimit: 50000,
      }).catch(() => [] as StatusData[]),
    ]);

  // Build most-recent-per-device maps
  const fuelByDevice     = latestPerDevice(fuelStatusData);
  const odometerByDevice = latestPerDevice(
    odometerFallbackData.filter((s) => {
      const val = parseFloat(String(s.data));
      return !isNaN(val) && val > 0;
    })
  );
  const batteryByDevice = latestPerDevice(
    [...batteryStatusData, ...(batteryExtData ?? [])].filter((s) => {
      const val = parseFloat(String(s.data));
      return !isNaN(val) && val >= BATTERY_MIN_V && val <= BATTERY_MAX_V;
    })
  );

  // ── Pre-index lookups so the per-device compose below stays O(n), not O(n²). ──
  // At 3000+ devices, scanning faultData/users/groups *inside* devices.map() was tens of
  // millions of comparisons per load (e.g. 3000 devices × 10000 faults = 30M), which froze
  // the main thread for seconds on every 10s refresh ("page unresponsive"). Building these
  // Maps in a single pass makes each lookup O(1).
  const faultsByDevice = new Map<string, FaultData[]>();
  faultData.forEach((f) => {
    const id = f.device?.id;
    if (!id) return;
    const list = faultsByDevice.get(id);
    if (list) list.push(f);
    else faultsByDevice.set(id, [f]);
  });

  const usersById = new Map<string, User>();
  users.forEach((u) => usersById.set(u.id, u));

  const groupsById = new Map<string, Group>();
  groups.forEach((g) => groupsById.set(g.id, g));

  // 6. Compose VehicleData records
  return devices.map((device) => {
    const dsi = deviceStatusInfoMap.get(device.id);
    const deviceStatus = (dsi?.statusData ?? []).sort(
      (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
    );
    const deviceFaults = faultsByDevice.get(device.id) ?? [];

    // Driver name
    let driverName = 'Unassigned';
    if (dsi?.driver?.id && dsi.driver.id !== 'UnknownDriverId') {
      const driverUser = usersById.get(dsi.driver.id);
      if (driverUser) {
        driverName =
          `${driverUser.firstName ?? ''} ${driverUser.lastName ?? ''}`.trim() ||
          driverUser.name ||
          'Unassigned';
      }
    }

    // Group — resolve the device's own (small) group list against the prebuilt map.
    const deviceGroups = device.groups ?? [];
    let group: Group | null = null;
    for (const dg of deviceGroups) {
      const g = groupsById.get(dg.id);
      if (g) { group = g; break; }
    }

    // Engine state + speed (rule #1: use DSI.isDriving)
    let engineRunning = false;
    let currentSpeed = 0;
    if (dsi) {
      engineRunning = dsi.isDriving === true;
      currentSpeed = dsi.speed != null ? Math.round(dsi.speed) : 0;
      if (currentSpeed < 0 || currentSpeed > 200) currentSpeed = 0;
    } else {
      const speedStatus = findDiagnosticValue(deviceStatus, SPEED_FALLBACK_IDS);
      const ignitionStatus = findDiagnosticValue(deviceStatus, IGNITION_FALLBACK_IDS);
      if (speedStatus) {
        const sv = parseFloat(String(speedStatus.data));
        if (sv > 1 && sv < 200) { engineRunning = true; currentSpeed = Math.round(sv); }
      }
      if (!engineRunning && ignitionStatus) {
        const val = ignitionStatus.data;
        engineRunning = val === 1 || Number(val) > 0;
      }
    }

    // Odometer: prefer DSI statusData (most-recent, already in memory) over the bulk fallback.
    // 'odometer' name-search catches databases where the ID differs from the standard KnownId
    // (e.g. GPS-based odometer reported under a fleet-specific or locale-specific diagnostic).
    const odometerFromDSI = findDiagnosticValue(deviceStatus, [
      DiagnosticOdometerAdjustmentId,
      DiagnosticOdometerId,
      'odometer',
    ]);
    const odometerRecord  = odometerFromDSI ?? odometerByDevice.get(device.id) ?? null;
    const fuelRecord      = fuelByDevice.get(device.id);
    const batteryRecord   = batteryByDevice.get(device.id);

    const vehicleFaults = deviceFaults.filter((f) => f.source === 'vehicle');
    // Sum total trigger counts across all active vehicle faults.
    // One fault code fired 149 times = 149, which determines health just like 149 unique codes.
    const vehicleTriggerTotal = vehicleFaults.reduce((sum, f) => sum + (f.count ?? 1), 0);
    let healthStatus: VehicleData['healthStatus'] = 'healthy';
    if (vehicleTriggerTotal > 5)      healthStatus = 'critical';
    else if (vehicleTriggerTotal > 0) healthStatus = 'warning';

    return {
      device,
      healthStatus,
      speed: currentSpeed,
      engineRunning,
      faultCount: deviceFaults.length,
      vehicleFaultCount: vehicleTriggerTotal,
      faults: deviceFaults,
      odometer: odometerRecord != null
        ? Math.round(parseFloat(String(odometerRecord.data)) / 1000)
        : null,
      fuelLevel: normalizeFuelLevel(fuelRecord ? fuelRecord.data : null),
      battery: batteryRecord != null
        ? parseFloat(String(batteryRecord.data)).toFixed(1)
        : null,
      driver: driverName,
      group: group ? group.name : 'No Group',
      vin: device.vehicleIdentificationNumber ?? 'N/A',
      lastCommunication: dsi?.dateTime
        ? new Date(dsi.dateTime)
        : deviceStatus.length > 0
        ? new Date(deviceStatus[0].dateTime)
        : null,
      serialNumber: device.serialNumber,
    } satisfies VehicleData;
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────

function latestPerDevice(records: StatusData[]): Map<string, StatusData> {
  const map = new Map<string, StatusData>();
  // Cache each kept record's parsed timestamp so we parse every dateTime at most once.
  // (The old code re-parsed the incumbent on every comparison — up to 2 Date parses per
  // record across ~200k status records per load.)
  const tsById = new Map<string, number>();
  records.forEach((s) => {
    const id = s.device?.id;
    if (!id) return;
    const ts = new Date(s.dateTime).getTime();
    const existingTs = tsById.get(id);
    if (existingTs === undefined || ts > existingTs) {
      map.set(id, s);
      tsById.set(id, ts);
    }
  });
  return map;
}
